import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Plus, LifeBuoy, Loader2 } from 'lucide-react';
import CreateCaseModal from './CreateCaseModal';

// One contextual support control for a customer, booking or order: the case
// reference when one already exists, or a way to raise one when it doesn't.
// The most recent case wins; anything older is one click away in the queue.
export default function CaseSupportLink({
  customerEmail,
  customerName,
  customerPhone,
  bookingId,
  bookingRef,
  orderId,
  orderRef,
}) {
  const [creating, setCreating] = useState(false);

  const email = String(customerEmail || '').toLowerCase();
  const query = bookingId
    ? { linked_booking_id: bookingId }
    : orderId
      ? { linked_order_id: orderId }
      : email
        ? { customer_email: email }
        : null;

  const { data: cases = [], isLoading, refetch } = useQuery({
    queryKey: ['support-cases', bookingId || '', orderId || '', email],
    queryFn: () => base44.entities.Case.filter(query, '-created_date', 20),
    enabled: !!query,
  });

  if (!query) return null;

  const badgeCls =
    'inline-flex items-center gap-1.5 text-[10px] font-bold tracking-widest px-3 py-1.5 border border-primary/40 text-primary bg-primary/10 hover:bg-primary/20 transition-colors';

  if (isLoading) {
    return (
      <span className="inline-flex items-center text-white/30 text-[10px] font-bold tracking-widest px-3 py-1.5 border border-white/10">
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
      </span>
    );
  }

  const current = cases[0];

  if (!current) {
    return (
      <>
        <button
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-widest px-3 py-1.5 border border-white/10 text-white/50 hover:text-white hover:border-primary/50 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" /> CREATE SUPPORT CASE
        </button>
        {creating && (
          <CreateCaseModal
            customerName={customerName}
            customerEmail={customerEmail}
            customerPhone={customerPhone}
            bookingId={bookingId}
            bookingRef={bookingRef}
            orderId={orderId}
            orderRef={orderRef}
            onClose={() => setCreating(false)}
            onCreated={() => {
              setCreating(false);
              refetch();
            }}
          />
        )}
      </>
    );
  }

  return (
    <span className="inline-flex items-center gap-2">
      <Link to={`/admin/crm/cases/${current.id}`} className={badgeCls} title={current.subject || ''}>
        <LifeBuoy className="w-3.5 h-3.5" /> {current.reference_code}
      </Link>
      {cases.length > 1 && email && (
        <Link
          to={`/admin/crm?customer=${encodeURIComponent(email)}`}
          className="text-white/35 hover:text-white text-[10px] font-bold tracking-widest transition-colors"
        >
          +{cases.length - 1} MORE
        </Link>
      )}
    </span>
  );
}